import { Inline, Stack, useResponsiveValue } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { FileRejection, useDropzone } from 'react-dropzone';
import { UploadFileIcon } from 'shared/icons';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { validateLoadsImportFile } from '../core/LoadsImportZone';
import { UploadDispatchSheetCardButton } from '../core/UploadDispatchSheetCardButton';

interface AddLoadDispatchSheetDropzoneProps {
  isParsing: boolean;
  onFileAccepted: (file: File) => void;
  onFileRejected?: (rejections: FileRejection[]) => void;
}

export function AddLoadDispatchSheetDropzone({
  isParsing,
  onFileAccepted,
  onFileRejected,
}: AddLoadDispatchSheetDropzoneProps) {
  const isMobile = useResponsiveValue(true, false);

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
    useDropzone({
      accept: {
        'application/pdf': ['.pdf'],
        'text/csv': ['.csv'],
        'application/vnd.ms-excel': ['.csv'],
      },
      onDrop: (
        files: File[],
        rejectedFiles: FileRejection[],
        event: DragEvent | Event,
      ) => {
        if (rejectedFiles.length) {
          onFileRejected?.(rejectedFiles);
        }

        const [file] = files;
        if (file) {
          if (event.type === 'drop') {
            trackLoadsEvent({
              name: 'Carrier Dropped Dispatch Sheet',
            });
          }
          onFileAccepted(file);
        }
      },
      validator: validateLoadsImportFile,
      multiple: false,
      noKeyboard: true,
      disabled: isParsing,
      noClick: true,
    });

  function handleUploadCardClick() {
    trackLoadsEvent({
      name: 'Carrier Clicked Upload Dispatch Sheet',
    });
    open();
  }

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} data-testid="add-load-dispatch-sheet-input" />

      <UploadDispatchSheetCardButton
        size="small"
        disabled={isParsing}
        $isDragActive={isDragActive}
        $isDragReject={isDragReject}
        onClick={handleUploadCardClick}
      >
        <Stack space="xxsmall" align="center">
          <Inline verticalAlign="center">
            <Box fontSize="larger">
              <UploadFileIcon color="primary" fontSize="inherit" />
            </Box>
            <TextBox variant="heading-4" color="blue">
              {isParsing
                ? 'Parsing dispatch sheet…'
                : isDragActive
                  ? isDragReject
                    ? 'This file type is not supported'
                    : 'Drop PDF or CSV to fill in load details'
                  : 'Upload Dispatch Sheet'}
            </TextBox>
          </Inline>
          <TextBox color="secondary" align="center">
            {isParsing
              ? 'Please wait while we parse your dispatch sheet'
              : isDragActive
                ? isDragReject
                  ? 'Only PDF and CSV files are allowed'
                  : 'Release to fill in load details'
                : isMobile
                  ? 'Tap to upload a PDF or CSV dispatch sheet'
                  : 'Drag & drop a PDF or CSV dispatch sheet, or click to upload'}
          </TextBox>
        </Stack>
      </UploadDispatchSheetCardButton>
    </div>
  );
}
